import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { setAIReminderPreference, purchaseAIGenerations, AI_FEATURE_CONFIG, incrementAIGenerationUsage, hasUnlockedAIFeatures } from './ai-utils';
import './AIUsageModal.css';
import { toast } from 'react-hot-toast';

const AIUsageModal = ({ 
  userId, 
  usageData, 
  onClose, 
  onPurchase,
  userPoints = 0,
  pointCost = AI_FEATURE_CONFIG.POINTS_REQUIRED 
}) => {
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [isUsingTrial, setIsUsingTrial] = useState(false);
  
  console.log("AI Usage Modal usageData:", usageData);
  
  const trialsRemaining = usageData?.trialsRemaining || 0;
  const trialUsageCount = usageData?.trialUsageCount || 0;
  const hasTrialsLeft = trialsRemaining > 0;
  const isFirstUse = trialUsageCount === 0;
  const isPurchased = usageData?.isPurchased || false;
  const expiryDate = usageData?.expiryDate ? new Date(usageData.expiryDate) : null;
  
  // If the user has already purchased AI features, we don't need to show the trial UI
  const hasActiveSubscription = isPurchased && expiryDate && expiryDate > new Date();
  const source = usageData?.source || '';
  const isAdminGranted = usageData?.isAdminGranted || false;
  
  console.log("Modal state:", { 
    hasTrialsLeft, 
    isPurchased, 
    hasActiveSubscription,
    source,
    isAdminGranted
  });
  
  if (expiryDate) {
    console.log("Expiry date:", expiryDate);
    console.log("Current date:", new Date());
    console.log("Is valid subscription:", expiryDate > new Date());
  }
  
  const handleClose = async () => {
    if (dontShowAgain) {
      await setAIReminderPreference(userId, false);
    }
    onClose();
  };

  const handleUseTrial = async () => {
    if (!userId) {
      toast.error('You must be logged in to use trials');
      return;
    }

    console.log('Using trial with user ID:', userId);
    setIsUsingTrial(true);
    
    try {
      // Double-check that user still has trials remaining before proceeding
      const accessCheck = await hasUnlockedAIFeatures(userId);
      if (!accessCheck.isTrial || accessCheck.trialsRemaining <= 0) {
        toast.error('No trials remaining. Please purchase with points.');
        setIsUsingTrial(false);
        return;
      }
      
      // Increment the trial usage count - this actually uses up a trial
      const result = await incrementAIGenerationUsage(userId);
      console.log('Increment result:', result);
      
      if (!result.success) {
        toast.error(`Failed to use trial: ${result.error || 'Unknown error'}`);
        setIsUsingTrial(false);
        return;
      }
      
      // If don't show again is checked, update the preference
      if (dontShowAgain) {
        await setAIReminderPreference(userId, false);
      }
      
      // Create a proper result object
      const purchaseResult = { 
        success: true, 
        isTrial: true,
        trialUsed: true,
        usageCount: result.usageCount,
        trialsRemaining: result.trialsRemaining,
        message: 'Trial use successful'
      };
      
      console.log('Calling onPurchase with:', purchaseResult);
      
      // Close modal and notify parent component
      toast.success('Trial usage successful!');
      
      // Call onPurchase BEFORE closing the modal
      // This ensures the parent component can receive the result
      onPurchase(purchaseResult);
      
    } catch (error) {
      console.error('Error using trial:', error);
      toast.error('Failed to use trial. Please try again.');
      setIsUsingTrial(false);
    }
  };
  
  const handlePurchase = async () => {
    if (!userId) {
      toast.error('You must be logged in to purchase');
      return;
    }
    
    setIsPurchasing(true);
    
    try {
      const result = await purchaseAIGenerations(userId, pointCost);
      
      if (result.success) {
        toast.success('You now have unlimited AI features for 2 months!');
        onPurchase(result);
        onClose();
      } else {
        if (result.error === 'Not enough points') {
          toast.error(`You need ${pointCost} points to purchase AI features (you have ${userPoints})`);
        } else {
          toast.error(`Error: ${result.error}`);
        }
      }
    } catch (error) {
      console.error('Error purchasing AI features:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsPurchasing(false);
    }
  };

  // Move useEffect to the top level - this fixes the React hooks rule violation
  useEffect(() => {
    let isComponentMounted = true;

    // Only run the auto-proceed logic if the user has an active subscription
    if (hasActiveSubscription && isComponentMounted) {
      console.log("Auto-proceeding with purchased features");

      // Set a small timeout to avoid UI flashing
      const timer = setTimeout(() => {
        if (isComponentMounted) {
          onPurchase({
            success: true,
            isPurchased: true,
            expiryDate: expiryDate,
            message: 'Already purchased',
            source: source,
            isAdminGranted: isAdminGranted
          });
          onClose();
        }
      }, 100);

      // Clean up function
      return () => {
        isComponentMounted = false;
        clearTimeout(timer);
      };
    }
    // Also auto-proceed if user has access but not through trial
    else if (usageData?.hasAccess && !usageData?.isTrial && isComponentMounted) {
      console.log("Auto-proceeding with unlocked features (not trial)");

      // Set a small timeout to avoid UI flashing
      const timer = setTimeout(() => {
        if (isComponentMounted) {
          onPurchase({
            success: true,
            isPurchased: true,
            message: 'Features unlocked through points',
            source: 'points_redemption'
          });
          onClose();
        }
      }, 100);

      // Clean up function
      return () => {
        isComponentMounted = false;
        clearTimeout(timer);
      };
    }
    // The cleanup function if we don't enter the if block
    return () => {
      isComponentMounted = false;
    };
  }, [hasActiveSubscription, expiryDate, source, isAdminGranted, onPurchase, onClose, usageData]);

  // If user has already purchased or has unlocked features, just show loading UI and let the useEffect handle the logic
  if (hasActiveSubscription || (usageData?.hasAccess && !usageData?.isTrial)) {
    let message = 'Your AI feature access is confirmed. Generating content...';
    let title = 'Processing Your Request';

    // Customize message based on source
    if (source === 'features_array' && isAdminGranted) {
      title = 'Admin Granted Access';
      message = 'You have AI feature access granted by an administrator. Generating content...';
    } else if (source === 'features_array' && !isAdminGranted) {
      title = 'Purchased Access';
      message = 'You have purchased AI features with your points. Generating content...';
    } else if (source === 'subcollection') {
      title = 'Premium Access';
      message = 'You have premium AI feature access. Generating content...';
    } else if (usageData?.hasAccess && !usageData?.isTrial) {
      title = 'Points Redemption Access';
      message = 'You have unlocked AI features by redeeming points. Generating content...';
    }

    console.log('User has active subscription or unlocked features, proceeding with AI generation');

    // Return a temporary loading state until the effect runs
    return (
      <div className="ai-usage-modal-overlay">
        <div className="ai-usage-modal">
          <div className="ai-usage-modal-header">
            <h2>{title}</h2>
          </div>
          <div className="ai-usage-modal-content" style={{ textAlign: 'center', padding: '20px' }}>
            <p>{message}</p>
            {expiryDate && (
              <p className="expiry-note" style={{ fontSize: '0.9em', opacity: 0.8, marginTop: '10px' }}>
                Access valid until: {expiryDate.toLocaleDateString()}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ai-usage-modal-overlay">
      <div className="ai-usage-modal">
        <div className="ai-usage-modal-header">
          <h2>
            {isFirstUse 
              ? 'Welcome to AI Features!'
              : hasTrialsLeft 
                ? 'AI Feature Trial' 
                : 'Free Trial Completed'}
          </h2>
          <button className="close-button" onClick={handleClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>
        
        <div className="ai-usage-modal-content">
          {isFirstUse ? (
            <div className="usage-message">
              <p>
                You have <span className="highlight">{AI_FEATURE_CONFIG.TRIAL_LIMIT} free trials</span> to 
                try out our AI-powered features:
              </p>
              <ul className="feature-list">
                <li>Generate AI responses to queries</li>
                <li>Get AI summaries of discussions</li>
                <li>Analyze patterns in user responses</li>
              </ul>
              <p>Would you like to use your first trial now?</p>
            </div>
          ) : hasTrialsLeft ? (
            <div className="usage-message">
              <p>
                You have <span className="highlight">{trialsRemaining} free trials</span> remaining out 
                of {AI_FEATURE_CONFIG.TRIAL_LIMIT}.
              </p>
              <div className="usage-progress">
                <div 
                  className="usage-bar" 
                  style={{ width: `${(trialUsageCount / AI_FEATURE_CONFIG.TRIAL_LIMIT) * 100}%` }}
                ></div>
              </div>
              <p className="usage-counter">
                {trialUsageCount} / {AI_FEATURE_CONFIG.TRIAL_LIMIT} trials used
              </p>
              <p>Would you like to use a trial now?</p>
            </div>
          ) : (
            <div className="usage-message usage-exceeded">
              <p>
                You have used all {AI_FEATURE_CONFIG.TRIAL_LIMIT} of your free trials.
              </p>
              <p>
                Purchase unlimited AI features for {AI_FEATURE_CONFIG.TRIAL_PERIOD_DAYS} days with your points.
              </p>
              <div className="points-info">
                <div className="points-cost">
                  <span className="cost-label">Cost:</span>
                  <span className="cost-value">{pointCost} points</span>
                </div>
                <div className="points-balance">
                  <span className="balance-label">Your balance:</span>
                  <span className={`balance-value ${userPoints < pointCost ? 'insufficient' : ''}`}>
                    {userPoints} points
                  </span>
                </div>
              </div>
            </div>
          )}
          
          {hasTrialsLeft && (
            <div className="reminder-checkbox">
              <label>
                <input 
                  type="checkbox" 
                  checked={dontShowAgain} 
                  onChange={(e) => setDontShowAgain(e.target.checked)}
                />
                Don't show this message again for remaining trials
              </label>
            </div>
          )}
          
          <div className="ai-usage-actions" style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
            <button 
              className="cancel-btn" 
              onClick={handleClose}
              style={{ padding: '10px 16px', borderRadius: '4px', background: '#f0f0f0', border: 'none' }}
            >
              Close
            </button>
            
            {hasTrialsLeft && (
              <button 
                id="use-trial-button"
                className="purchase-btn"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('Trial button clicked');
                  handleUseTrial();
                }}
                style={{
                  backgroundColor: '#22c55e',
                  color: 'white',
                  fontWeight: 'bold',
                  padding: '12px 24px',
                  fontSize: '1.1rem',
                  cursor: 'pointer',
                  border: 'none',
                  borderRadius: '4px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  flex: '1'
                }}
                disabled={isUsingTrial}
              >
                {isUsingTrial ? 'Processing...' : 'Yes, Use Trial Now →'}
              </button>
            )}
            
            <button 
              className="purchase-btn"
              onClick={handlePurchase}
              disabled={isPurchasing || userPoints < pointCost}
              style={{
                backgroundColor: userPoints >= pointCost ? '#22c55e' : '#9ca3af',
                color: 'white',
                fontWeight: 'bold',
                padding: '12px 24px',
                fontSize: '1.1rem',
                cursor: userPoints >= pointCost ? 'pointer' : 'not-allowed',
                border: 'none',
                borderRadius: '4px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                flex: '1'
              }}
            >
              {isPurchasing ? 'Processing...' : `Purchase (${pointCost} points)`}
            </button>
          </div>
          
          {!hasTrialsLeft && userPoints < pointCost && (
            <p className="earn-more-points">
              Need more points? Complete more queries or help others to earn points!
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIUsageModal; 